import { supabase, TABLES } from '../config/supabase';
import advancedEmailAutomationService from './advancedEmailAutomationService';
import logger from '../utils/logger';

// Validation utilities for system stability
class ValidationUtils {
  static validateEmail(email) {
    if (!email || typeof email !== 'string') return false;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email) && email.length <= 254;
  }
  
  static validateCampaignId(campaignId) {
    if (!campaignId || typeof campaignId !== 'string') return false;
    // Allow alphanumeric, hyphens, underscores
    const campaignRegex = /^[a-zA-Z0-9_-]+$/;
    return campaignRegex.test(campaignId) && campaignId.length <= 100;
  }
  
  static sanitizeString(str) {
    if (!str || typeof str !== 'string') return '';
    return str
      .replace(/<script[^>]*>.*?<\/script>/gi, '')
      .replace(/<iframe[^>]*>.*?<\/iframe>/gi, '')
      .replace(/javascript:/gi, '')
      .replace(/on\w+\s*=/gi, '')
      .trim();
  }
}

class EmailAutomationService {
  constructor() {
    this.isProcessing = false;
    this.settings = null;
    // N8N webhook URL from environment
    this.webhookUrl = process.env.REACT_APP_N8N_EMAIL_AUTOMATION_WEBHOOK_URL || '';
    this.webhookEnabled = true; // Enable for production consistency
    this.processingLock = false; // Add explicit lock for race condition prevention
    this.lastProcessingTime = null;
  }

  /**
   * Initialize the service and load settings
   */
  async initialize() {
    try {
      await this.loadSettings();
      logger.log('📧 Email Automation Service V2 initialized with N8N webhook integration');
    } catch (error) {
      logger.error('Failed to initialize Email Automation Service:', error);
    }
  }

  /**
   * Load automation settings
   */
  async loadSettings() {
    try {
      const { data, error } = await supabase
        .from('email_automation_settings')
        .select('*')
        .limit(1)
        .single();

      if (error && error.code !== 'PGRST116') {
        throw error;
      }

      if (!data) {
        // Create default settings if none exist
        const { data: newSettings, error: createError } = await supabase
          .from('email_automation_settings')
          .insert({
            is_enabled: true,
            max_daily_sends: 100,
            current_daily_sends: 0,
            monitoring_enabled: true
          })
          .select()
          .single();

        if (createError) throw createError;
        this.settings = newSettings;
      } else {
        this.settings = data;
        // Set webhook URL from constructor (no database update needed)
        this.settings.webhook_url = this.webhookUrl;
      }

      logger.log('⚙️ Settings loaded:', {
        enabled: this.settings.is_enabled,
        webhook_url: this.webhookUrl,
        max_daily_sends: this.settings.max_daily_sends,
        monitoring_enabled: this.settings.monitoring_enabled
      });
    } catch (error) {
      logger.error('Error loading settings:', error);
    }
  }

  /**
   * Reset daily counter if needed
   */
  async resetDailyCounter() {
    try {
      const today = new Date().toISOString().split('T')[0];
      
      if (this.settings.last_reset_date !== today) {
        await supabase
          .from('email_automation_settings')
          .update({
            current_daily_sends: 0,
            last_reset_date: today,
            updated_at: new Date().toISOString()
          })
          .eq('id', this.settings.id);

        this.settings.current_daily_sends = 0;
        this.settings.last_reset_date = today;
        
        logger.log('🔄 Daily counter reset for', today);
      }
    } catch (error) {
      logger.error('Error resetting daily counter:', error);
    }
  }

  /**
   * Process lead event and trigger automation with advanced system integration
   */
  async processLeadEvent(leadEvent) {
    try {
      if (!this.settings?.is_enabled) {
        logger.log('⏸️ Email automation is disabled');
        return;
      }

      // Input validation for system stability
      if (!ValidationUtils.validateEmail(leadEvent.lead_email)) {
        logger.error('❌ Invalid lead email:', leadEvent.lead_email);
        return;
      }
      
      if (leadEvent.campaign_id && !ValidationUtils.validateCampaignId(leadEvent.campaign_id)) {
        logger.error('❌ Invalid campaign ID:', leadEvent.campaign_id);
        return;
      }
      
      if (!leadEvent.event_type || typeof leadEvent.event_type !== 'string') {
        logger.error('❌ Invalid event type:', leadEvent.event_type);
        return;
      }

      logger.log('📨 Processing lead event (V5 Advanced):', {
        email: leadEvent.lead_email,
        event: leadEvent.event_type,
        campaign: leadEvent.campaign_name,
        campaign_id: leadEvent.campaign_id
      });

      // Use advanced system for open events
      if (leadEvent.event_type === 'open') {
        logger.log('🚀 Using advanced open event processing');
        const result = await advancedEmailAutomationService.processOpenEventAdvanced(leadEvent);
        logger.log('✅ Advanced processing result:', result);
        return result;
      }

      // NOTE: Reply events no longer stop sequences automatically.
      // All follow-up sequences must send all steps at their configured times.
      // Only leads that are MANUALLY stopped via Inbox will stop receiving emails.
      if (leadEvent.event_type === 'reply') {
        logger.log('📧 Reply detected - sequences will continue sending (manual stop only)');
        return;
      }

      // For other event types, use legacy processing
      if (leadEvent.event_type !== 'open' && leadEvent.event_type !== 'reply') {
        logger.log('⏭️ Using legacy processing for event type:', leadEvent.event_type);
        return await this.processLegacyEvent(leadEvent);
      }

    } catch (error) {
      logger.error('Error processing lead event:', error);
    }
  }

  /**
   * Legacy event processing for backward compatibility
   */
  async processLegacyEvent(leadEvent) {
    try {
      // Get lead data
      const { data: lead, error: leadError } = await supabase
        .from(TABLES.LEADS)
        .select('*')
        .eq('email', leadEvent.lead_email)
        .single();

      if (leadError || !lead) {
        logger.error('Lead not found:', leadEvent.lead_email);
        return;
      }

      // Check if campaign is active for automation
      const campaignId = leadEvent.campaign_id || lead.campaign_id;
      if (campaignId) {
        const { data: isActive, error: activeError } = await supabase
          .rpc('is_campaign_active', {
            p_campaign_id: campaignId
          });

        if (activeError) {
          logger.error('Error checking campaign status:', activeError);
          return;
        }

        if (!isActive) {
          logger.log(`⏭️ Campaign ${campaignId} is not active for automation`);
          return;
        }
      }

      // Get active template for this campaign
      const template = await this.getActiveTemplate(campaignId);
      if (!template) {
        logger.log('⚠️ No active template found for campaign:', campaignId);
        return;
      }

      // Check if automation should be executed
      const shouldExecute = await this.shouldExecuteAutomation(template, lead, leadEvent);
      if (!shouldExecute) {
        logger.log('⏭️ Automation conditions not met for:', lead.email);
        return;
      }

      // Schedule automation with campaign validation and inbox routing
      await this.scheduleAutomationWithInboxRouting(template, lead, leadEvent);

    } catch (error) {
      logger.error('Error in legacy event processing:', error);
    }
  }

  /**
   * Check if lead has replied
   */
  async checkIfLeadHasReplied(leadEmail) {
    try {
      const { data: messages, error } = await supabase
        .from(TABLES.MESSAGES)
        .select('id, created_at')
        .eq('lead_email', leadEmail)
        .eq('sender_type', 'received')
        .order('created_at', { ascending: false })
        .limit(1);

      if (error) {
        logger.error('Error checking replies:', error);
        return false;
      }

      return messages && messages.length > 0;
    } catch (error) {
      logger.error('Error checking if lead replied:', error);
      return false;
    }
  }

  /**
   * Get active template for specific campaign
   */
  async getActiveTemplate(campaignId = null) {
    try {
      // If campaign ID is provided, get campaign-specific template
      if (campaignId) {
        const { data: templateData, error } = await supabase
          .rpc('get_template_for_campaign', {
            p_campaign_id: campaignId
          });

        if (!error && templateData && templateData.length > 0) {
          // Convert the function result to template format
          const template = {
            id: templateData[0].template_id,
            name: templateData[0].template_name,
            content_template: templateData[0].content_template,
            custom_subject: templateData[0].custom_subject,
            delay_minutes: templateData[0].delay_minutes || (templateData[0].delay_days * 1440),
            webhook_url: templateData[0].webhook_url,
            is_global: templateData[0].is_global,
            is_active: true,
            trigger_event: 'open'
          };
          return template;
        }
      }

      // First try to get the active template from settings
      if (this.settings?.active_template_id) {
        const { data: template, error } = await supabase
          .from('email_automation_templates')
          .select('*')
          .eq('id', this.settings.active_template_id)
          .eq('is_active', true)
          .single();

        if (!error && template) {
          return template;
        }
      }

      // Fallback to any active global template
      const { data: templates, error } = await supabase
        .from('email_automation_templates')
        .select('*')
        .eq('is_active', true)
        .eq('trigger_event', 'open')
        .eq('is_global', true)
        .order('template_priority', { ascending: true })
        .order('created_at', { ascending: false })
        .limit(1);

      if (error) {
        logger.error('Error fetching active template:', error);
        return null;
      }

      return templates && templates.length > 0 ? templates[0] : null;
    } catch (error) {
      logger.error('Error getting active template:', error);
      return null;
    }
  }

  /**
   * Check if automation should be executed
   */
  async shouldExecuteAutomation(template, lead, triggerEvent) {
    try {
      const conditions = template.conditions || {};

      // Check if lead has minimum score
      if (conditions.min_score && lead.score < conditions.min_score) {
        logger.log(`⏭️ Lead score ${lead.score} below minimum ${conditions.min_score}`);
        return false;
      }

      // Check if lead has replied (if required)
      if (conditions.require_no_reply) {
        const hasReplied = await this.checkIfLeadHasReplied(lead.email);
        if (hasReplied) {
          logger.log('⏭️ Lead has already replied, skipping automation');
          return false;
        }
      }

      // Check max executions per lead
      const maxExecutions = conditions.max_executions_per_lead || 1;
      const { data: existingExecutions, error } = await supabase
        .from('email_automation_executions')
        .select('id')
        .eq('lead_email', lead.email)
        .eq('template_id', template.id)
        .not('status', 'eq', 'cancelled');

      if (error) {
        logger.error('Error checking existing executions:', error);
        return false;
      }

      if (existingExecutions && existingExecutions.length >= maxExecutions) {
        logger.log(`⏭️ Max executions (${maxExecutions}) reached for lead:`, lead.email);
        return false;
      }

      return true;
    } catch (error) {
      logger.error('Error checking automation conditions:', error);
      return false;
    }
  }

  /**
   * Schedule automation with campaign validation, delay, and inbox routing
   */
  async scheduleAutomationWithInboxRouting(template, lead, triggerEvent) {
    try {
      const campaignId = triggerEvent.campaign_id || lead.campaign_id;
      logger.log('📅 Scheduling automation with inbox routing for:', lead.email, 'Campaign:', campaignId);
      
      const delayDays = template.delay_minutes ? Math.round(template.delay_minutes / 1440) : (this.settings.default_delay_days || 7);

      // Use the enhanced database function for campaign validation and inbox routing
      const { data: executionId, error } = await supabase
        .rpc('schedule_automation_with_inbox_routing', {
          p_template_id: template.id,
          p_lead_id: lead.id,
          p_lead_email: lead.email,
          p_trigger_event_id: triggerEvent.id,
          p_campaign_id: campaignId,
          p_delay_days: delayDays
        });

      if (error) {
        if (error.message.includes('not active for automation')) {
          logger.log(`⏭️ Campaign ${campaignId} is not active for automation`);
          return null;
        }
        throw error;
      }

      // Get the created execution record with inbox routing info
      const { data: execution, error: fetchError } = await supabase
        .from('email_automation_executions')
        .select('*, inbox_route, sender_email_used')
        .eq('id', executionId)
        .single();

      if (fetchError) throw fetchError;

      // Process template content and update execution
      const emailContent = this.processTemplateContent(template.content_template, lead, triggerEvent);
      const emailSubject = this.processTemplateContent(
        template.custom_subject || template.subject_template, 
        lead, 
        triggerEvent
      );

      // Update execution with processed content
      const { error: updateError } = await supabase
        .from('email_automation_executions')
        .update({
          email_subject: emailSubject,
          email_content: emailContent
        })
        .eq('id', executionId);

      if (updateError) throw updateError;
      
      logger.log('📝 Template processed with inbox routing:', {
        subject: emailSubject,
        delay_days: delayDays,
        campaign_id: campaignId,
        inbox_route: execution.inbox_route,
        sender_email: execution.sender_email_used,
        scheduled_at: execution.scheduled_at
      });

      logger.log(`✅ Automation scheduled with ID: ${executionId} for ${delayDays} days later`);
      logger.log(`📧 Will send via ${execution.inbox_route} (${execution.sender_email_used})`);
      logger.log(`⏰ Will execute on: ${new Date(execution.scheduled_at).toLocaleString()}`);

      return { 
        ...execution, 
        email_subject: emailSubject, 
        email_content: emailContent 
      };
    } catch (error) {
      logger.error('Error scheduling automation with inbox routing:', error);
      throw error;
    }
  }

  /**
   * Process pending executions (called by cron job or scheduler)
   */
  async processPendingExecutions() {
    // Prevent concurrent processing
    if (this.processingLock) {
      logger.log('⏭️ Execution processing already in progress, skipping');
      return;
    }

    // Prevent too frequent processing
    const now = Date.now();
    if (this.lastProcessingTime && (now - this.lastProcessingTime) < 30000) {
      logger.log('⏭️ Processing too frequent, waiting...');
      return;
    }

    try {
      this.processingLock = true;
      this.lastProcessingTime = now;
      this.isProcessing = true;
      logger.log('🔄 Processing pending automation executions...');

      // Reset daily counter if needed
      await this.resetDailyCounter();

      // Get pending executions that are due
      const { data: executions, error } = await supabase
        .from('email_automation_executions')
        .select(`
          *,
          email_automation_templates(*)
        `)
        .eq('status', 'pending')
        .lte('scheduled_send_time', new Date().toISOString())  // ✅ FIXED: Check scheduled_send_time, not scheduled_at
        .order('scheduled_send_time', { ascending: true })
        .limit(50); // Process in batches

      if (error) {
        logger.error('Error fetching pending executions:', error);
        return;
      }

      if (!executions || executions.length === 0) {
        logger.log('✅ No pending executions to process');
        return;
      }

      logger.log(`📋 Found ${executions.length} pending executions to process`);

      for (const execution of executions) {
        try {
          await this.executeAutomation(execution.id);
          // Small delay between executions to avoid overwhelming the webhook
          await new Promise(resolve => setTimeout(resolve, 1000));
        } catch (error) {
          logger.error(`Error executing automation ${execution.id}:`, error);
        }
      }

      logger.log('✅ Finished processing pending executions');
    } catch (error) {
      logger.error('Error processing pending executions:', error);
    } finally {
      this.isProcessing = false;
      this.processingLock = false;
    }
  }

  /**
   * Execute a scheduled automation
   */
  async executeAutomation(executionId) {
    try {
      // Get execution details
      const { data: execution, error: execError } = await supabase
        .from('email_automation_executions')
        .select(`
          *,
          email_automation_templates(*)
        `)
        .eq('id', executionId)
        .eq('status', 'pending')
        .single();

      if (execError || !execution) {
        logger.error('Execution not found or already processed:', executionId);
        return;
      }

      // Get lead data
      const { data: lead, error: leadError } = await supabase
        .from(TABLES.LEADS)
        .select('*')
        .eq('email', execution.lead_email)
        .single();

      if (leadError || !lead) {
        throw new Error(`Lead not found: ${execution.lead_email}`);
      }

      // NOTE: Reply detection no longer cancels pending executions.
      // All follow-up sequences must send all steps at their configured times.
      // Only leads that are MANUALLY stopped via Inbox will stop receiving emails.

      // Check if parent sequence is manually stopped — only 'stopped' status blocks execution
      if (execution.sequence_id) {
        const { data: parentSeq } = await supabase
          .from('email_automation_sequences')
          .select('status')
          .eq('id', execution.sequence_id)
          .single();

        if (parentSeq && parentSeq.status === 'stopped') {
          logger.log(`🛑 Skipping ${execution.lead_email} — sequence manually stopped`);
          await supabase
            .from('email_automation_executions')
            .update({
              status: 'cancelled',
              error_message: 'Sequence manually stopped',
              updated_at: new Date().toISOString()
            })
            .eq('id', executionId);
          return;
        }
      }

      // Check daily send limit
      await this.loadSettings();
      if (this.settings.current_daily_sends >= this.settings.max_daily_sends) {
        logger.log('📊 Daily send limit reached, postponing automation');
        // Reschedule for tomorrow — update scheduled_send_time so processPendingExecutions won't pick it up again
        const tomorrow = new Date();
        tomorrow.setDate(tomorrow.getDate() + 1);
        tomorrow.setHours(9, 0, 0, 0);
        
        await supabase
          .from('email_automation_executions')
          .update({
            scheduled_send_time: tomorrow.toISOString(),
            scheduled_at: tomorrow.toISOString(),
            updated_at: new Date().toISOString()
          })
          .eq('id', executionId);
        return;
      }

      logger.log(`🚀 Executing automation for ${execution.lead_email} via N8N webhook`);

      // Send via N8N webhook
      await this.sendViaWebhook(execution, lead);

      // Update execution status
      await supabase
        .from('email_automation_executions')
        .update({
          status: 'sent',
          executed_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .eq('id', executionId);

      // Update daily send counter
      await supabase
        .from('email_automation_settings')
        .update({
          current_daily_sends: this.settings.current_daily_sends + 1,
          updated_at: new Date().toISOString()
        })
        .eq('id', this.settings.id);

      this.settings.current_daily_sends += 1;

      logger.log(`✅ Automation executed successfully for ${execution.lead_email}`);
    } catch (error) {
      logger.error('Error executing automation:', error);
      
      // Update execution status to failed
      await supabase
        .from('email_automation_executions')
        .update({
          status: 'failed',
          error_message: error.message,
          updated_at: new Date().toISOString()
        })
        .eq('id', executionId);
    }
  }

  /**
   * Send automation email via N8N webhook with timeout and retry logic
   */
  async sendViaWebhook(execution, lead, retryCount = 0) {
    const maxRetries = 3;
    const timeoutMs = 30000; // 30 seconds
    
    try {
      if (!this.webhookEnabled) {
        logger.log('⚠️ Webhook disabled - skipping email send (development mode)');
        return { ok: true, status: 200, message: 'Webhook disabled' };
      }
      
      logger.log(`🔗 Sending automation email via N8N webhook... (attempt ${retryCount + 1}/${maxRetries + 1})`);
      
      // Find the original sender email from lead_events raw_data
      const { data: leadEvent, error: eventError } = await supabase
        .from(TABLES.LEAD_EVENTS)
        .select('raw_data, reply_subject, campaign_id, workspace_id')
        .eq('lead_email', execution.lead_email)
        .not('raw_data', 'is', null)
        .order('created_at', { ascending: false })
        .limit(1)
        .single();

      // Use lead_events email_account, or fallback to execution.original_inbox_email (set by rescheduler)
      let originalSenderEmail = leadEvent?.raw_data?.email_account || execution.original_inbox_email;
      
      if (!originalSenderEmail) {
        throw new Error(`No sender email found for ${execution.lead_email} — no lead_events raw_data.email_account and no original_inbox_email on execution`);
      }
      
      // Validate required fields and provide fallbacks
      if (!execution.lead_email) {
        throw new Error('Lead email is required for webhook payload');
      }
      
      if (!execution.email_subject || !execution.email_content) {
        throw new Error('Email subject and content are required for webhook payload');
      }

      // Prepare webhook payload for N8N
      const actualStep = execution.sequence_step || 1;
      const webhookPayload = {
        action: 'send_automation_email',
        execution_id: execution.id,
        sequence_step: actualStep,
        timestamp: new Date().toISOString(),
        lead_data: {
          email: execution.lead_email,
          first_name: lead?.first_name || '',
          last_name: lead?.last_name || null,
          company: lead?.company || '',
          campaign_id: leadEvent?.campaign_id || null,
          campaign_name: lead?.campaign_name || 'Unknown Campaign',
          workspace_id: leadEvent?.workspace_id || null
        },
        email_data: {
          recipient_email: execution.lead_email,
          sender_email: originalSenderEmail,
          subject: execution.email_subject,
          html_body: execution.email_content,
          automation_type: `sequence_step_${actualStep}`,
          original_inbox: originalSenderEmail
        },
        automation_data: {
          template_id: execution.template_id || null,
          scheduled_at: execution.scheduled_at || new Date().toISOString(),
          executed_at: new Date().toISOString(),
          sequence_id: execution.sequence_id || null,
          sequence_step: actualStep
        }
      };

      const webhookUrl = execution.email_automation_templates?.webhook_url || this.settings.webhook_url || this.webhookUrl;

      logger.log('=== SENDING N8N AUTOMATION WEBHOOK ===');
      logger.log('Lead Email:', execution.lead_email);
      logger.log('Sender Email:', originalSenderEmail);
      logger.log('Subject:', execution.email_subject);
      logger.log('Webhook URL:', webhookUrl);
      logger.log('Execution ID:', execution.id);
      logger.log('=====================================');

      const startTime = Date.now();
      
      // Create AbortController for timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
      
      const response = await fetch(webhookUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(webhookPayload),
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      const responseTime = Date.now() - startTime;

      const responseText = await response.text();
      let responseData = null;
      
      try {
        responseData = JSON.parse(responseText);
      } catch (e) {
        responseData = { raw_response: responseText };
      }

      // Log webhook call
      await this.logWebhookCall(
        execution.id,
        webhookUrl,
        webhookPayload,
        response.status,
        responseText,
        responseTime,
        response.ok ? null : `HTTP ${response.status}: ${response.statusText}`
      );

      if (!response.ok) {
        throw new Error(`N8N webhook failed: ${response.status} - ${responseText}`);
      }
      
      logger.log('✅ Automation email sent successfully via N8N webhook:', responseData);

      // Update execution with webhook response
      await supabase
        .from('email_automation_executions')
        .update({
          updated_at: new Date().toISOString()
        })
        .eq('id', execution.id);

      return responseData;
    } catch (error) {
      logger.error(`Error sending via N8N webhook (attempt ${retryCount + 1}):`, error);
      
      // Check if this is a retryable error
      const isRetryable = error.name === 'AbortError' || 
                         error.code === 'ECONNRESET' || 
                         error.code === 'ENOTFOUND' || 
                         error.code === 'ETIMEDOUT' ||
                         (error.status && error.status >= 500);
      
      // Retry logic
      if (isRetryable && retryCount < maxRetries) {
        const delay = Math.pow(2, retryCount) * 1000; // Exponential backoff
        logger.log(`⏳ Retrying webhook call in ${delay}ms...`);
        await new Promise(resolve => setTimeout(resolve, delay));
        return this.sendViaWebhook(execution, lead, retryCount + 1);
      }
      
      // Log failed webhook call
      await this.logWebhookCall(
        execution.id,
        this.webhookUrl,
        { error: 'Failed to prepare payload' },
        null,
        null,
        null,
        `${error.message} (after ${retryCount + 1} attempts)`
      );
      
      throw error;
    }
  }

  /**
   * Log webhook call for monitoring
   */
  async logWebhookCall(executionId, webhookUrl, requestPayload, responseStatus, responseBody, responseTimeMs, errorMessage) {
    try {
      await supabase
        .from('email_automation_webhook_logs')
        .insert({
          execution_id: executionId,
          webhook_url: webhookUrl,
          request_payload: requestPayload,
          response_status: responseStatus,
          response_body: responseBody,
          response_time_ms: responseTimeMs,
          error_message: errorMessage
        });
    } catch (error) {
      logger.error('Error logging webhook call:', error);
    }
  }

  /**
   * Cancel an execution
   */
  async cancelExecution(executionId, reason = 'Cancelled') {
    try {
      await supabase
        .from('email_automation_executions')
        .update({
          status: 'cancelled',
          error_message: reason,
          updated_at: new Date().toISOString()
        })
        .eq('id', executionId);

      logger.log(`❌ Execution ${executionId} cancelled: ${reason}`);
    } catch (error) {
      logger.error('Error cancelling execution:', error);
    }
  }

  /**
   * Process template content with lead data
   */
  processTemplateContent(template, lead, triggerEvent) {
    if (!template) return '';
    
    let content = template;
    
    // Replace lead placeholders
    content = content.replace(/{{first_name}}/g, lead.first_name || 'there');
    content = content.replace(/{{last_name}}/g, lead.last_name || '');
    content = content.replace(/{{email}}/g, lead.email || '');
    content = content.replace(/{{company}}/g, lead.company || 'your company');
    content = content.replace(/{{campaign_name}}/g, lead.campaign_name || triggerEvent?.campaign_name || 'our campaign');
    content = content.replace(/{{source}}/g, lead.source || 'our outreach');
    
    return content;
  }

  /**
   * Get analytics and campaign statistics
   */
  async getAnalytics() {
    try {
      const { data: executions, error } = await supabase
        .from('email_automation_executions')
        .select(`
          *,
          email_automation_templates(name)
        `);

      if (error) throw error;

      const analytics = {
        total_scheduled: executions.length,
        total_sent: executions.filter(e => e.status === 'sent').length,
        total_failed: executions.filter(e => e.status === 'failed').length,
        total_cancelled: executions.filter(e => e.status === 'cancelled').length,
        total_pending: executions.filter(e => e.status === 'pending').length,
        success_rate: 0,
        by_campaign: {},
        by_template: {},
        recent_executions: executions
          .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
          .slice(0, 10)
      };

      if (analytics.total_scheduled > 0) {
        analytics.success_rate = Math.round((analytics.total_sent / analytics.total_scheduled) * 100);
      }

      // Group by campaign
      executions.forEach(execution => {
        const campaign = execution.campaign_id || 'Unknown';
        if (!analytics.by_campaign[campaign]) {
          analytics.by_campaign[campaign] = {
            total: 0,
            sent: 0,
            failed: 0,
            cancelled: 0,
            pending: 0
          };
        }
        analytics.by_campaign[campaign].total++;
        analytics.by_campaign[campaign][execution.status]++;
      });

      // Group by template
      executions.forEach(execution => {
        const template = execution.email_automation_templates?.name || 'Unknown';
        if (!analytics.by_template[template]) {
          analytics.by_template[template] = {
            total: 0,
            sent: 0,
            failed: 0,
            cancelled: 0,
            pending: 0
          };
        }
        analytics.by_template[template].total++;
        analytics.by_template[template][execution.status]++;
      });

      return analytics;
    } catch (error) {
      logger.error('Error getting analytics:', error);
      return null;
    }
  }

  /**
   * Get campaign statistics
   */
  async getCampaignStats(campaignId = null) {
    try {
      let query = supabase
        .from('email_automation_executions')
        .select('campaign_id, status');

      if (campaignId) {
        query = query.eq('campaign_id', campaignId);
      }

      const { data: executions, error } = await query;

      if (error) throw error;

      const stats = {};
      
      executions.forEach(execution => {
        const campaign = execution.campaign_id || 'Unknown';
        if (!stats[campaign]) {
          stats[campaign] = {
            total_scheduled: 0,
            total_sent: 0,
            total_failed: 0,
            total_cancelled: 0,
            total_pending: 0,
            success_rate: 0
          };
        }
        
        stats[campaign].total_scheduled++;
        stats[campaign][`total_${execution.status}`]++;
      });

      // Calculate success rates
      Object.keys(stats).forEach(campaign => {
        const campaignStats = stats[campaign];
        if (campaignStats.total_scheduled > 0) {
          campaignStats.success_rate = Math.round(
            (campaignStats.total_sent / campaignStats.total_scheduled) * 100
          );
        }
      });

      return campaignId ? stats[campaignId] : stats;
    } catch (error) {
      logger.error('Error getting campaign stats:', error);
      return null;
    }
  }

  /**
   * Set automation enabled/disabled
   */
  async setEnabled(enabled) {
    try {
      await supabase
        .from('email_automation_settings')
        .update({
          is_enabled: enabled,
          updated_at: new Date().toISOString()
        })
        .eq('id', this.settings.id);

      this.settings.is_enabled = enabled;
      logger.log(`📧 Email automation ${enabled ? 'enabled' : 'disabled'}`);
      
      return true;
    } catch (error) {
      logger.error('Error setting automation status:', error);
      return false;
    }
  }

  /**
   * Set active template
   */
  async setActiveTemplate(templateId) {
    try {
      await supabase
        .from('email_automation_settings')
        .update({
          active_template_id: templateId,
          updated_at: new Date().toISOString()
        })
        .eq('id', this.settings.id);

      this.settings.active_template_id = templateId;
      logger.log(`📧 Active template set to: ${templateId}`);
      
      return true;
    } catch (error) {
      logger.error('Error setting active template:', error);
      return false;
    }
  }

  /**
   * Update webhook URL and enable webhooks
   */
  async updateWebhookUrl(newUrl) {
    try {
      this.settings.webhook_url = newUrl;
      this.webhookUrl = newUrl;
      this.webhookEnabled = true; // Enable webhooks when URL is set
      logger.log(`🔗 Webhook URL updated to: ${newUrl}`);
      logger.log('✅ Webhooks enabled');
      
      return true;
    } catch (error) {
      logger.error('Error updating webhook URL:', error);
      return false;
    }
  }

  /**
   * Enable or disable webhook functionality
   */
  setWebhookEnabled(enabled) {
    this.webhookEnabled = enabled;
    logger.log(`🔗 Webhooks ${enabled ? 'enabled' : 'disabled'}`);
  }
}

// Create singleton instance
const emailAutomationService = new EmailAutomationService();

export default emailAutomationService;